import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import '../../styles/teachering-components.css';

// 애니메이션 CSS 추가
const animationStyles = `
  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

// 스타일 태그 추가
if (typeof document !== 'undefined' && !document.querySelector('#qr-modal-animations')) {
  const style = document.createElement('style');
  style.id = 'qr-modal-animations';
  style.textContent = animationStyles;
  document.head.appendChild(style);
}

const QRModal = ({ 
  show, 
  onHide, 
  qrValue, 
  title = 'QR 코드',
  description = '',
  qrLevel = 'H',
  showCopyButton = false,
  copyButtonText = '링크 복사',
  enableDrag = true
}) => {
  const [copied, setCopied] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });

  // 윈도우 리사이즈 이벤트
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 드래그 이벤트 리스너 설정
  useEffect(() => {
    if (!isDragging || windowWidth < 576) return;

    const handleMouseMove = (e) => {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // 화면 경계 내에서만 이동하도록 제한
      const modalWidth = 750;
      const modalHeight = 600;
      const maxX = window.innerWidth - modalWidth;
      const maxY = window.innerHeight - modalHeight;
      
      setModalPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, windowWidth]);

  // 모달이 열릴 때 초기 위치 설정
  useEffect(() => {
    if (show && enableDrag) {
      const modalWidth = 750;
      const modalHeight = 600;
      const centerX = (window.innerWidth - modalWidth) / 2;
      const centerY = (window.innerHeight - modalHeight) / 2;
      setModalPosition({ x: centerX, y: centerY });
    }
  }, [show, enableDrag]);

  if (!show) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onHide();
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(qrValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('링크 복사 실패:', err);
    }
  };

  const handleMouseDown = (e) => {
    if (!enableDrag || windowWidth < 576) return;
    
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    e.preventDefault();
  };

  // 반응형 QR 크기 계산 - 해상도에 비례하여 자동 조절
  const calculateQRSize = () => {
    if (windowWidth < 576) {
      // 모바일: 화면 너비의 70%, 최소 200px, 최대 400px
      const mobileSize = Math.min(Math.max(windowWidth * 0.7, 200), 400);
      return Math.floor(mobileSize);
    } else if (windowWidth < 768) {
      // 태블릿: 화면 너비의 50%, 최소 300px, 최대 450px
      const tabletSize = Math.min(Math.max(windowWidth * 0.5, 300), 450);
      return Math.floor(tabletSize);
    } else {
      // 데스크탑: 화면 너비와 높이 중 작은 값의 40%, 최소 350px, 최대 600px
      const desktopBase = Math.min(windowWidth, windowHeight) * 0.4;
      const desktopSize = Math.min(Math.max(desktopBase, 350), 600);
      return Math.floor(desktopSize);
    }
  };
  
  const responsiveQRSize = calculateQRSize();

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: enableDrag && windowWidth >= 576 ? 'flex-start' : 'center',
        alignItems: enableDrag && windowWidth >= 576 ? 'flex-start' : 'center',
        zIndex: 1060,
        userSelect: isDragging ? 'none' : 'auto',
        cursor: isDragging ? 'grabbing' : 'default'
      }}
      onClick={handleOverlayClick}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: 'var(--radius-lg)',
          width: windowWidth < 576 ? '95%' : '750px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          animation: 'fadeInScale 0.3s ease-out',
          overflow: 'hidden',
          position: enableDrag && windowWidth >= 576 ? 'absolute' : 'relative',
          left: enableDrag && windowWidth >= 576 ? `${modalPosition.x}px` : 'auto',
          top: enableDrag && windowWidth >= 576 ? `${modalPosition.y}px` : 'auto',
          transform: enableDrag && windowWidth >= 576 ? 'none' : 'none',
          cursor: enableDrag && windowWidth >= 576 ? (isDragging ? 'grabbing' : 'grab') : 'default'
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={handleMouseDown}
      >
        {/* 모달 헤더 */}
        <div style={{
          padding: 'var(--spacing-md)',
          borderBottom: '1px solid var(--gray-200)',
          position: 'relative',
          backgroundColor: 'var(--gray-50)',
          cursor: enableDrag && windowWidth >= 576 ? (isDragging ? 'grabbing' : 'grab') : 'default'
        }}>
          <button
            onClick={onHide}
            style={{
              position: 'absolute',
              top: 'var(--spacing-md)',
              right: 'var(--spacing-md)',
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: 'var(--font-xl)',
              cursor: 'pointer',
              color: 'var(--gray-400)',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-full)',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'var(--gray-100)';
              e.target.style.color = 'var(--gray-600)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = 'var(--gray-400)';
            }}
          >
            ×
          </button>
          <h3 style={{
            textAlign: 'center',
            margin: '0',
            fontSize: 'var(--font-lg)',
            fontWeight: '600',
            color: 'var(--gray-800)',
            paddingRight: '40px'
          }}>
            {title}
            {enableDrag && windowWidth >= 576 && (
              <span style={{
                fontSize: 'var(--font-sm)',
                color: 'var(--gray-500)',
                fontWeight: '400',
                display: 'block',
                marginTop: '4px'
              }}>
                드래그하여 이동 가능
              </span>
            )}
          </h3>
        </div>

        {/* 모달 바디 */}
        <div style={{
          padding: 'var(--spacing-lg)',
          textAlign: 'center',
          backgroundColor: 'white'
        }}>
          <div style={{ 
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center'
          }}>
            {qrValue ? (
              <QRCodeSVG
                value={qrValue}
                size={responsiveQRSize}
                level={qrLevel}
                bgColor="#ffffff"
                fgColor="#000000"
                includeMargin={true}
                style={{ 
                  margin: '0 auto 20px', 
                  width: `${responsiveQRSize}px`,
                  height: `${responsiveQRSize}px`,
                  minWidth: `${responsiveQRSize}px`,
                  minHeight: `${responsiveQRSize}px`,
                  objectFit: 'contain',
                  border: '1px solid var(--gray-200)',
                  borderRadius: 'var(--radius-md)'
                }}
              />
            ) : (
              <div style={{
                width: responsiveQRSize,
                height: responsiveQRSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--gray-200)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--gray-50)',
                margin: '0 auto 20px',
                color: 'var(--gray-500)',
                fontSize: 'var(--font-sm)'
              }}>
                QR 코드 생성 중...
              </div>
            )}
            
            {description && (
              <p style={{ 
                fontSize: 'var(--font-base)',
                color: 'var(--gray-600)',
                margin: '0',
                lineHeight: '1.5'
              }}>
                {description}
              </p>
            )}
            
            {/* 링크 복사 버튼 */}
            {showCopyButton && qrValue && (
              <div style={{ marginTop: 'var(--spacing-md)' }}>
                <button
                  onClick={handleCopyLink}
                  style={{
                    backgroundColor: copied ? 'var(--green)' : 'var(--primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    padding: 'var(--spacing-xs) var(--spacing-md)',
                    fontSize: 'var(--font-sm)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontWeight: '500'
                  }}
                  onMouseOver={(e) => {
                    if (!copied) {
                      e.target.style.backgroundColor = 'var(--primary-dark)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!copied) {
                      e.target.style.backgroundColor = 'var(--primary)';
                    }
                  }}
                >
                  {copied ? '복사 완료!' : copyButtonText}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRModal;